import { useState, useCallback, useEffect } from 'react';
import './WalkieTalkiePuzzle.css';

interface WalkieTalkiePuzzleProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  targetCode: string; // Ex: "SLS" pour Short-Long-Short
  hint: string;
  successMessage: string;
}

// Le code "AMI" en morse simplifié :
// A = ●━ (Short-Long)
// M = ━━ (Long-Long)
// I = ●● (Short-Short)
// Total: S-L-L-L-S-S

export function WalkieTalkiePuzzle({
  isOpen,
  onClose,
  onSuccess,
  targetCode,
  hint,
  successMessage,
}: WalkieTalkiePuzzleProps) {
  const [playerInput, setPlayerInput] = useState<string[]>([]);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isButtonPressed, setIsButtonPressed] = useState<'short' | 'long' | null>(null);

  // Reset when opened
  useEffect(() => {
    if (isOpen) {
      setPlayerInput([]);
      setIsSuccess(false);
      setShowError(false);
    }
  }, [isOpen]);

  // Handle button press
  const handlePress = useCallback((type: 'S' | 'L') => {
    if (isSuccess) return;

    setIsButtonPressed(type === 'S' ? 'short' : 'long');
    setTimeout(() => setIsButtonPressed(null), 200);

    const newInput = [...playerInput, type];
    setPlayerInput(newInput);

    // Check if input matches target
    const currentInput = newInput.join('');

    if (currentInput === targetCode) {
      // Success!
      setIsSuccess(true);
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } else if (currentInput.length >= targetCode.length) {
      // Wrong code - reset
      setShowError(true);
      setTimeout(() => {
        setPlayerInput([]);
        setShowError(false);
      }, 1000);
    } else if (!targetCode.startsWith(currentInput)) {
      // Wrong path - reset
      setShowError(true);
      setTimeout(() => {
        setPlayerInput([]);
        setShowError(false);
      }, 800);
    }
  }, [playerInput, targetCode, isSuccess, onSuccess]);

  // Clear input
  const handleClear = useCallback(() => {
    setPlayerInput([]);
    setShowError(false);
  }, []);

  if (!isOpen) return null;

  // Convert code to visual display
  const renderCode = (code: string[]) => {
    return code.map((c, i) => (
      <span key={i} className={`morse-symbol ${c === 'S' ? 'short' : 'long'}`}>
        {c === 'S' ? '●' : '━'}
      </span>
    ));
  };

  // Convert target code to visual hint
  const renderTargetHint = (code: string) => {
    return code.split('').map((c, i) => (
      <span key={i} className={`morse-symbol hint ${c === 'S' ? 'short' : 'long'}`}>
        {c === 'S' ? '●' : '━'}
      </span>
    ));
  };

  return (
    <div className="walkie-overlay" onClick={(e) => e.stopPropagation()}>
      <div className={`walkie-container ${isSuccess ? 'success' : ''} ${showError ? 'error' : ''}`}>
        {/* Close button */}
        <button className="walkie-close" onClick={onClose}>✕</button>

        {/* Title */}
        <div className="walkie-title">
          📻 Walkie-Talkie
        </div>

        {/* Hint from Erica */}
        <div className="walkie-hint">
          {hint}
        </div>

        {/* Target code display */}
        <div className="walkie-target">
          <span className="label">Code à envoyer :</span>
          <div className="code-display target">
            {renderTargetHint(targetCode)}
          </div>
        </div>

        {/* Walkie-Talkie device */}
        <div className="walkie-device">
          {/* Antenna */}
          <div className="walkie-antenna"></div>

          {/* Screen showing player input */}
          <div className="walkie-screen">
            <div className="screen-label">Ton message :</div>
            <div className="code-display player">
              {playerInput.length > 0 ? renderCode(playerInput) : <span className="placeholder">...</span>}
            </div>
          </div>

          {/* Speaker grille */}
          <div className="walkie-speaker">
            <div className="speaker-line"></div>
            <div className="speaker-line"></div>
            <div className="speaker-line"></div>
            <div className="speaker-line"></div>
          </div>

          {/* Buttons */}
          <div className="walkie-buttons">
            <button
              className={`walkie-btn short ${isButtonPressed === 'short' ? 'pressed' : ''}`}
              onClick={() => handlePress('S')}
              disabled={isSuccess}
            >
              <span className="btn-symbol">●</span>
              <span className="btn-label">COURT</span>
            </button>
            <button
              className={`walkie-btn long ${isButtonPressed === 'long' ? 'pressed' : ''}`}
              onClick={() => handlePress('L')}
              disabled={isSuccess}
            >
              <span className="btn-symbol">━</span>
              <span className="btn-label">LONG</span>
            </button>
          </div>

          {/* Clear button */}
          <button className="walkie-clear" onClick={handleClear} disabled={isSuccess}>
            🔄 Effacer
          </button>
        </div>

        {/* Success message */}
        {isSuccess && (
          <div className="walkie-success">
            <div className="success-icon">✨</div>
            <div className="success-text">{successMessage}</div>
          </div>
        )}

        {/* Error feedback */}
        {showError && (
          <div className="walkie-error">
            ❌ Mauvais code ! Réessaie...
          </div>
        )}

        {/* Legend */}
        <div className="walkie-legend">
          <span>● = Court (clic rapide)</span>
          <span>━ = Long (signal continu)</span>
        </div>
      </div>
    </div>
  );
}
